import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { fetchGet, useFetchGetConditional } from "Services/api";
import { DateTime } from "luxon";
import { getOffersByDomainsByPeriodData } from "Components/Stats/Data/Offres/Domaines";
import { Calendar } from "primereact/calendar";
import NivoPie from "Components/Stats/Charts/NivoPie";
import Margin from "Components/Stats/Margin";

/**
 * Composant graphique des offres par domaine par mois
 *
 * Un bouton permet de choisir la période sur laquelle on veut afficher les données (défaut: 6 derniers mois)
 *
 * Un graphique en barres affiche le nombre d'offres par domaine sur une période choisie.
 *
 * {@link getOffersByDomainsByPeriodData} : Fonction qui formate les données pour le graphique du nombre d'offres publiées par domaine sur une période
 *
 * {@link NivoPie} : Composant générique de graphique en camembert implémenté avec la librairie Nivo.
 *
 * @param {Object} props - Props Redux
 * @param {String} props.universityName - Nom de l'université
 * @param {Object} props.auth - Objet Redux d'authentification
 * @param {Object} props.secondaryTables - Objet Redux des tables secondaires
 * @returns {JSX.Element} - Composant OffersByDomainByPeriod
 */
const OffersByDomainByPeriod = (props) => {
  /* Données pour graphique des offres par catégorie par mois*/
  const [offersByDomainPeriod, setOffersByDomainPeriod] = useState([
    DateTime.local()
      .startOf("month")
      .minus({ months: 6 })
      .plus({ days: 1 })
      .toJSDate(),
    DateTime.local().endOf("month").minus({ months: 1 }).toJSDate(),
  ]);

  const offerDomains = useFetchGetConditional(
    "/domains",
    props.auth.token,
    props.secondaryTables.domainsData
  );

  const [graphData, setGraphData] = useState([]);

  /**
   * Mise à jour des données pour les offres par catégorie par mois (publiées sur une période)
   */
  async function updateOffersCountDomains() {
    let requestData = await fetchGet(
      "/offers/stats/count/domains",
      props.auth.token,
      {
        // Filtre période, posté après la date de début et avant la date de fin
        "postedAt[after]": offersByDomainPeriod[0].toISOString().slice(0, 10),
        "postedAt[before]": offersByDomainPeriod[1].toISOString().slice(0, 10),
      }
    );

    setGraphData(getOffersByDomainsByPeriodData(requestData));
  }

  useEffect(async () => {
    // Dès que la période est définie, et correcte,
    // On met à jour les données
    if (
      offersByDomainPeriod[0] instanceof Date &&
      !isNaN(offersByDomainPeriod[0]) &&
      offersByDomainPeriod[1] instanceof Date &&
      !isNaN(offersByDomainPeriod[1])
    )
      await updateOffersCountDomains();
  }, [offersByDomainPeriod]);

  return (
    <React.Fragment>
      <div className="calendar">
        <label>Période choisie</label>
        <Calendar
          value={offersByDomainPeriod}
          onChange={(e) => {
            if (e.value[0] && e.value[1]) {
              let beforeDate = DateTime.fromJSDate(e.value[0]);
              let afterDate = DateTime.fromJSDate(e.value[1]).plus({
                months: 1,
              });
              setOffersByDomainPeriod([
                beforeDate.toJSDate(),
                afterDate.toJSDate(),
              ]);
            } else {
              setOffersByDomainPeriod(e.value);
            }
          }}
          showIcon
          view="month"
          dateFormat="mm/yy"
          selectionMode="range"
          maxDate={DateTime.local().minus({ months: 1 }).toJSDate()}
        />
      </div>
      <Margin position="bottom" size="s" />
      {offerDomains.loaded && (
        <NivoPie
          displayLegends={false}
          data={graphData}
          keys={offerDomains.data.map((domain) => domain.name)}
          title={
            "Nombre d'offres par domaine par période sur l'université " +
            props.universityName
          }
        />
      )}
    </React.Fragment>
  );
};

OffersByDomainByPeriod.propTypes = {
  universityName: PropTypes.string.isRequired,
  auth: PropTypes.object.isRequired,
  secondaryTables: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  secondaryTables: state.secondaryTables,
});

const mapDispatchToProps = () => ({});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OffersByDomainByPeriod);
